import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Drawer } from "@sylviq/ui-react-web/components/ui/drawer";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import { PageHeader } from "@sylviq/ui-react-web/components/ui/page-header";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@sylviq/ui-react-web/components/ui/table";
import { Link } from "@tanstack/react-router";
import { type FormEvent, useEffect, useState } from "react";
import { AccountPicker } from "@/components/Pickers";
import {
  type BankRuleMatchField,
  type BankRuleMatchType,
  type BankRuleRecord,
  createBankRule,
  listBankRules,
  updateBankRule,
} from "@/lib/api";

function toErrorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

export function BankRulesPage() {
  const [rules, setRules] = useState<BankRuleRecord[] | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [priority, setPriority] = useState(100);
  const [matchField, setMatchField] =
    useState<BankRuleMatchField>("description");
  const [matchType, setMatchType] = useState<BankRuleMatchType>("contains");
  const [matchValue, setMatchValue] = useState("");
  const [accountId, setAccountId] = useState("");
  const [autoReconcile, setAutoReconcile] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [readError, setReadError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    setReadError(null);
    listBankRules()
      .then(setRules)
      .catch((cause: unknown) => setReadError(toErrorMessage(cause)))
      .finally(() => setLoading(false));
  };

  const reload = () => {
    listBankRules()
      .then(setRules)
      .catch((cause: unknown) => setError(toErrorMessage(cause)));
  };

  useEffect(load, []);

  const clearForm = () => {
    setEditingId(null);
    setName("");
    setPriority(100);
    setMatchField("description");
    setMatchType("contains");
    setMatchValue("");
    setAccountId("");
    setAutoReconcile(false);
  };

  const payload = () => ({
    name: name.trim(),
    priority,
    match_field: matchField,
    match_type: matchType,
    match_value: matchValue.trim(),
    ...(accountId ? { account_id: accountId } : {}),
    auto_reconcile: autoReconcile,
  });

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!name.trim() || !matchValue.trim()) {
      setError("Give the rule a name and something to match on.");
      return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      if (editingId) {
        await updateBankRule(editingId, { ...payload(), is_active: true });
        setNotice("Rule saved.");
      } else {
        const created = await createBankRule(payload());
        setNotice(`Rule created (${created.id}).`);
      }
      clearForm();
      setEditorOpen(false);
      reload();
    } catch (cause) {
      setError(toErrorMessage(cause));
    } finally {
      setBusy(false);
    }
  };

  const editRule = (rule: BankRuleRecord) => {
    setEditorOpen(true);
    setEditingId(rule.id);
    setName(rule.name);
    setPriority(rule.priority);
    setMatchField(rule.match_field);
    setMatchType(rule.match_type);
    setMatchValue(rule.match_value);
    setAccountId(rule.account_id ?? "");
    setAutoReconcile(rule.auto_reconcile);
    setNotice(null);
    setError(null);
  };

  const setActive = async (rule: BankRuleRecord, is_active: boolean) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await updateBankRule(rule.id, {
        name: rule.name,
        priority: rule.priority,
        match_field: rule.match_field,
        match_type: rule.match_type,
        match_value: rule.match_value,
        ...(rule.account_id ? { account_id: rule.account_id } : {}),
        auto_reconcile: rule.auto_reconcile,
        is_active,
      });
      setNotice(is_active ? "Rule enabled." : "Rule disabled.");
      reload();
    } catch (cause) {
      setError(toErrorMessage(cause));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid gap-6">
      <PageHeader
        title="Bank rules"
        description="Rules match imported bank lines by description or reference. Apply them from a bank account."
      />
      <div className="grid gap-4">
        <p className="text-sm text-[var(--muted-foreground)]">
          A disabled rule stays on this list and is skipped the next time rules
          run. Apply them from a{" "}
          <Link
            to="/banking"
            className="font-medium text-[var(--action)] underline"
          >
            bank account
          </Link>
          .
        </p>
        <div className="actions-row">
          <Button
            type="button"
            disabled={busy}
            onClick={() => {
              if (editingId) clearForm();
              setError(null);
              setEditorOpen(true);
            }}
          >
            New bank rule
          </Button>
          {editingId && !editorOpen ? (
            <Button
              type="button"
              variant="secondary"
              onClick={() => setEditorOpen(true)}
            >
              Continue editing
            </Button>
          ) : null}
        </div>
        {readError ? (
          <div
            role="alert"
            className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
          >
            <p>Couldn&apos;t load bank rules: {readError}</p>
            <div className="mt-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={load}
                disabled={loading}
              >
                Retry
              </Button>
            </div>
          </div>
        ) : loading ? (
          <p className="text-sm text-[var(--muted-foreground)]">
            Loading rules…
          </p>
        ) : rules && rules.length === 0 ? (
          <p className="text-sm text-[var(--muted-foreground)]">No rules yet.</p>
        ) : rules ? (
          <div className="surface overflow-hidden rounded-2xl">
            <div className="table-scroll">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Rule</TableHead>
                    <TableHead>Match</TableHead>
                    <TableHead>Priority</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rules.map((rule) => (
                    <TableRow key={rule.id}>
                      <TableCell className="font-medium">{rule.name}</TableCell>
                      <TableCell>
                        {rule.match_field} {rule.match_type} “{rule.match_value}”
                      </TableCell>
                      <TableCell>{rule.priority}</TableCell>
                      <TableCell>
                        {rule.is_active ? "Active" : "Disabled"}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-2">
                          <Button
                            type="button"
                            variant="secondary"
                            disabled={busy}
                            onClick={() => editRule(rule)}
                          >
                            Edit
                          </Button>
                          {rule.is_active ? (
                            <Button
                              type="button"
                              variant="secondary"
                              disabled={busy}
                              onClick={() => void setActive(rule, false)}
                            >
                              Disable
                            </Button>
                          ) : (
                            <Button
                              type="button"
                              variant="secondary"
                              disabled={busy}
                              onClick={() => void setActive(rule, true)}
                            >
                              Enable
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        ) : null}
        {error && !editorOpen ? (
          <div
            role="alert"
            className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
          >
            {error}
          </div>
        ) : null}
        {notice ? (
          <output className="surface block rounded-xl border border-[var(--success)] p-4 text-sm text-[var(--success)]">
            {notice}
          </output>
        ) : null}
        <Drawer
          open={editorOpen}
          onOpenChange={(next) => {
            if (!busy) setEditorOpen(next);
          }}
          title={editingId ? "Edit bank rule" : "New bank rule"}
          description="Choose which imported lines this rule matches. Matching still runs from the bank account."
          className="w-[min(46rem,100%)]"
          footer={
            <>
              <Button
                type="button"
                variant="secondary"
                disabled={busy}
                onClick={() => setEditorOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" form="bank-rule-form" disabled={busy}>
                {busy ? "Saving…" : editingId ? "Save rule" : "Create rule"}
              </Button>
            </>
          }
        >
          {error ? (
            <p role="alert" className="mb-3 text-sm text-[var(--danger)]">
              {error}
            </p>
          ) : null}
          <form id="bank-rule-form" onSubmit={submit} className="grid gap-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <label
                htmlFor="rule-name"
                className="grid gap-1 text-xs font-medium"
              >
                Rule name
                <Input
                  id="rule-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  placeholder="e.g. Office rent"
                  disabled={busy}
                />
              </label>
              <label
                htmlFor="rule-priority"
                className="grid gap-1 text-xs font-medium"
              >
                Priority (lower runs first)
                <Input
                  id="rule-priority"
                  type="number"
                  value={priority}
                  onChange={(e) => setPriority(Number(e.target.value))}
                  disabled={busy}
                />
              </label>
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <label className="grid gap-1 text-xs font-medium">
                Match field
                <select
                  className="select"
                  value={matchField}
                  onChange={(e) =>
                    setMatchField(e.target.value as BankRuleMatchField)
                  }
                  disabled={busy}
                  aria-label="Match field"
                >
                  <option value="description">Description</option>
                  <option value="reference">Reference</option>
                </select>
              </label>
              <label className="grid gap-1 text-xs font-medium">
                Match type
                <select
                  className="select"
                  value={matchType}
                  onChange={(e) =>
                    setMatchType(e.target.value as BankRuleMatchType)
                  }
                  disabled={busy}
                  aria-label="Match type"
                >
                  <option value="contains">Contains</option>
                  <option value="equals">Equals</option>
                  <option value="starts_with">Starts with</option>
                  <option value="ends_with">Ends with</option>
                </select>
              </label>
              <label
                htmlFor="rule-value"
                className="grid gap-1 text-xs font-medium"
              >
                Match value
                <Input
                  id="rule-value"
                  value={matchValue}
                  onChange={(e) => setMatchValue(e.target.value)}
                  required
                  placeholder="e.g. LANDLORD LTD"
                  disabled={busy}
                />
              </label>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 sm:items-end">
              <div className="grid gap-1 text-xs font-medium">
                <label htmlFor="rule-account">Target account (optional)</label>
                <AccountPicker
                  id="rule-account"
                  value={accountId}
                  onChange={setAccountId}
                  disabled={busy}
                />
              </div>
              <label
                htmlFor="rule-auto"
                className="flex items-center gap-2 text-sm font-normal"
              >
                <input
                  id="rule-auto"
                  type="checkbox"
                  checked={autoReconcile}
                  onChange={(e) => setAutoReconcile(e.target.checked)}
                  disabled={busy}
                />
                Reconcile automatically on match
              </label>
            </div>
          </form>
        </Drawer>
      </div>
    </div>
  );
}
